import type { MembershipPlan } from './membershipService';

export type BillingInterval = 'monthly' | 'annual';

export interface PlanDetailSection {
  title: string;
  body: string;
}

export interface PlanDetailExtra {
  idealFor: string;
  highlights: string[];
  sections: PlanDetailSection[];
  faqs: { q: string; a: string }[];
}

const DETAIL_BY_TIER: Record<MembershipPlan['tier'], PlanDetailExtra> = {
  bronze: {
    idealFor:
      'Newcomers who want steady guidance, checklists, and community support without a heavy monthly commitment.',
    highlights: [
      'App-based pathway Q&A whenever you need direction',
      'IRCC checklist library so you never miss a document',
      'Quarterly RCIC consult to stay on track',
      '10% member pricing on CWY service orders',
    ],
    sections: [
      {
        title: 'How support works',
        body: 'Ask immigration pathway questions in the CWY app portal and get clear next steps. Monthly policy alerts keep you informed when IRCC rules change that may affect your case.',
      },
      {
        title: 'Consultations',
        body: 'Includes 1 RCIC consultation per quarter. Book through the app when you need a professional check-in on strategy, timelines, or document readiness.',
      },
      {
        title: 'Community',
        body: 'Join Hindi / Punjabi community groups for peer tips, shared experiences, and reminders that keep your application moving.',
      },
    ],
    faqs: [
      {
        q: 'Can I upgrade later?',
        a: 'Yes. You can move to Silver or Gold anytime. Billing adjusts on your next cycle once checkout is live.',
      },
      {
        q: 'Does the discount stack?',
        a: 'Member discounts apply automatically on eligible CWY service orders. Promo codes that conflict may be adjusted at checkout.',
      },
    ],
  },
  silver: {
    idealFor:
      'Applicants with active deadlines who need faster replies, document reviews, and priority access.',
    highlights: [
      'Priority chat with a 4-hour response SLA',
      'Skip-queue walk-in access when available',
      'Deadline monitoring for PFL, WP, and hearings',
      '2 pre-submit document reviews every month',
    ],
    sections: [
      {
        title: 'Priority care',
        body: 'Silver members get faster chat routing and skip-queue walk-in privileges so urgent questions do not wait behind general traffic.',
      },
      {
        title: 'Deadline monitoring',
        body: 'We track key immigration deadlines (PFL responses, work permit renewals, hearing dates) and nudge you before windows close.',
      },
      {
        title: 'Document reviews',
        body: 'Two document reviews per month help catch gaps before you submit to IRCC — reducing refusals and rework.',
      },
    ],
    faqs: [
      {
        q: 'What is the response SLA?',
        a: 'Priority chat aims for a first reply within 4 business hours. Complex legal questions may still need a booked RCIC consult.',
      },
      {
        q: 'What’s included from Bronze?',
        a: 'Everything in Bronze carries forward — pathway Q&A, checklists, alerts, community access — plus Silver-only perks.',
      },
    ],
  },
  gold: {
    idealFor:
      'Complex or high-stakes cases that need a named handler, unlimited reviews, and emergency coverage.',
    highlights: [
      'Named dedicated case handler',
      'Unlimited document reviews & RCIC consults',
      'Emergency response under 1 hour (business hours)',
      '25% off services + annual immigration health check',
    ],
    sections: [
      {
        title: 'Dedicated handling',
        body: 'A named case handler knows your file, timeline, and goals — so you are not re-explaining your story on every interaction.',
      },
      {
        title: 'Unlimited reviews & consults',
        body: 'Iterate on packages as often as needed and book RCIC consultations without monthly caps. Built for multi-pathway or refusal-sensitive files.',
      },
      {
        title: 'Cross-border coordination',
        body: 'India / cross-border coordination and an annual immigration health check keep long-horizon plans (PR, family, business) aligned year-round.',
      },
    ],
    faqs: [
      {
        q: 'What counts as an emergency?',
        a: 'Time-sensitive refusals, removal risk, hearing tomorrow, or employer deadlines during business hours. Non-urgent questions still use normal channels.',
      },
      {
        q: 'Is EMI fee really waived?',
        a: 'Yes on eligible CWY EMI / payment-plan fees for Gold members. Government filing fees are separate and still apply.',
      },
    ],
  },
};

export function getPlanDetailExtra(tier: MembershipPlan['tier']): PlanDetailExtra {
  return DETAIL_BY_TIER[tier];
}

export function formatMembershipMoney(amount: number, currency: string): string {
  const symbol = currency === 'CAD' || currency === 'USD' ? '$' : `${currency} `;
  return `${symbol}${amount.toFixed(amount % 1 === 0 ? 0 : 2)}`;
}

export interface HubPitchCopy {
  title: string;
  body: string;
  bullets: string[];
}

/** Gap pitch when the user has no membership. */
export function getNoMembershipPitch(override?: Partial<HubPitchCopy>): HubPitchCopy {
  return {
    title: override?.title || 'What you’re missing without membership',
    body:
      override?.body ||
      'Without a plan, pathway questions wait in the general queue, document checklists stay DIY, and you pay full price on CWY services. Membership closes those gaps.',
    bullets: override?.bullets?.length
      ? override.bullets
      : [
          'No guaranteed pathway Q&A or IRCC checklist library',
          'No scheduled RCIC consults or deadline monitoring',
          'No member discounts on eligible service orders',
        ],
  };
}

/** Upgrade pitch when already on Bronze or Silver. */
export function getUpgradePitch(
  currentTier: 'bronze' | 'silver',
  override?: Partial<HubPitchCopy>
): HubPitchCopy {
  if (currentTier === 'bronze') {
    return {
      title: override?.title || 'Unlock more with Silver or Gold',
      body:
        override?.body ||
        'Bronze covers the basics. Upgrade for faster replies, document reviews, and stronger case coverage.',
      bullets: override?.bullets?.length
        ? override.bullets
        : [
            'Silver: priority chat (4-hr SLA), deadline monitoring, 2 reviews / month',
            'Gold: dedicated case handler, unlimited reviews & RCIC consults',
            'Higher tiers unlock larger service discounts (15% → 25%)',
          ],
    };
  }
  return {
    title: override?.title || 'Go all-in with Gold',
    body:
      override?.body ||
      'Silver already gives you speed. Gold adds a named handler, unlimited reviews, and emergency response.',
    bullets: override?.bullets?.length
      ? override.bullets
      : [
          'Named dedicated case handler who knows your file',
          'Unlimited document reviews and RCIC consultations',
          'Emergency response under 1 hour + 25% off services',
        ],
  };
}

export function getGoldCompleteCopy(override?: Partial<HubPitchCopy>): HubPitchCopy {
  return {
    title: override?.title || 'You’re on our highest plan',
    body:
      override?.body ||
      'Gold includes dedicated handling, unlimited reviews, and our strongest member savings. No further upgrades are available.',
    bullets: override?.bullets || [],
  };
}

/** Which upgrade tiers to show for a live membership. */
export function getUpgradeTiers(
  currentTier: 'bronze' | 'silver' | 'gold' | undefined
): Array<'silver' | 'gold'> {
  if (currentTier === 'bronze') return ['silver', 'gold'];
  if (currentTier === 'silver') return ['gold'];
  return [];
}

/** Primary CTA label on the member home upgrade strip. */
export function getUpgradeCtaLabel(
  currentTier: 'bronze' | 'silver' | 'gold' | undefined
): string | null {
  if (currentTier === 'bronze') return 'Want to upgrade your membership?';
  if (currentTier === 'silver') return 'Want to upgrade your membership?';
  return null;
}
